"use client";

import { type Column } from "@tanstack/react-table";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ArrowDown, ArrowUp, ChevronsUpDown, EyeOff } from "lucide-react";

interface DataTableColumnHeaderProps<TData, TValue>
  extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>;
  title: string;
}

export default function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  className,
}: DataTableColumnHeaderProps<TData, TValue>) {
  if (!column.getCanSort() && !column.getCanHide()) {
    return (
      <div
        className={cn(
          "min-w-0 whitespace-normal break-words font-semibold leading-tight text-slate-700 dark:text-slate-300",
          className
        )}
      >
        {title}
      </div>
    );
  }

  return (
    <div className={cn("flex min-w-0 items-center", className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            aria-label={
              column.getIsSorted() === "desc"
                ? "Sorted descending. Click to sort ascending."
                : column.getIsSorted() === "asc"
                  ? "Sorted ascending. Click to sort descending."
                  : "Not sorted. Click to sort ascending."
            }
            variant="ghost"
            size="sm"
            className="-ml-2 flex h-auto min-h-7 w-full min-w-0 max-w-full items-start justify-start overflow-hidden rounded-md px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-200/80 data-[state=open]:bg-slate-200 dark:text-slate-300 dark:hover:bg-slate-700/80 dark:data-[state=open]:bg-slate-700"
          >
            <span className="min-w-0 flex-1 whitespace-normal break-words text-left leading-tight">
              {title}
            </span>
            {column.getCanSort() && column.getIsSorted() === "desc" ? (
              <ArrowDown
                className="ml-1.5 mt-0.5 size-3.5 shrink-0"
                aria-hidden="true"
              />
            ) : column.getIsSorted() === "asc" ? (
              <ArrowUp
                className="ml-1.5 mt-0.5 size-3.5 shrink-0"
                aria-hidden="true"
              />
            ) : (
              <ChevronsUpDown
                className="ml-1.5 mt-0.5 size-3.5 shrink-0"
                aria-hidden="true"
              />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {column.getCanSort() && (
            <>
              <DropdownMenuItem
                aria-label="Sort ascending"
                onClick={() => column.toggleSorting(false)}
              >
                <ArrowUp
                  className="mr-2 size-3.5 text-muted-foreground/70"
                  aria-hidden="true"
                />
                Asc
              </DropdownMenuItem>
              <DropdownMenuItem
                aria-label="Sort descending"
                onClick={() => column.toggleSorting(true)}
              >
                <ArrowDown
                  className="mr-2 size-3.5 text-muted-foreground/70"
                  aria-hidden="true"
                />
                Desc
              </DropdownMenuItem>
            </>
          )}
          {column.getCanSort() && column.getCanHide() && (
            <DropdownMenuSeparator />
          )}
          {column.getCanHide() && (
            <DropdownMenuItem
              aria-label="Hide column"
              onClick={() => column.toggleVisibility(false)}
            >
              <EyeOff
                className="mr-2 size-3.5 text-muted-foreground/70"
                aria-hidden="true"
              />
              Hide
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
